import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Badge, Button, Modal, Form } from 'react-bootstrap';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { dateTime } from './adminShared';

// Readable random password, generated in the browser (no look-alike characters).
const generatePassword = () => {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  return Array.from(crypto.getRandomValues(new Uint8Array(12)), b => alphabet[b % alphabet.length]).join('');
};

// Shows a password exactly once — it can't be looked up again — with ways to hand it over:
// copy it, copy it as a ready-to-paste message, or email it.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PasswordReveal = ({ title, userId, username, email: savedEmail, password, onClose, onEmailSaved }) => {
  const [copied, setCopied] = useState('');
  const [email, setEmail] = useState(savedEmail || '');
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState('');
  const [error, setError] = useState('');

  const copy = (key, text) => { navigator.clipboard?.writeText(text); setCopied(key); setTimeout(() => setCopied(''), 2000); };
  const message = `Usuario: ${username}\nContraseña temporal: ${password}\nEntra en: ${window.location.origin}`;

  const send = async () => {
    setSending(true); setError('');
    try {
      const address = email.trim().toLowerCase();
      if (address !== (savedEmail || '')) { await API.patch(`/users/${userId}`, { email: address }); onEmailSaved?.(); }
      await API.post(`/users/${userId}/email-password`, { password });
      setSentTo(address);
    } catch (err) {
      setError(err.response?.data?.message || 'No se pudo enviar el correo.');
    } finally { setSending(false); }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>{title}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>Contraseña temporal de <strong>{username}</strong>. <strong>No se puede volver a ver</strong> después de cerrar esta ventana; si se pierde, se genera otra. La persona deberá cambiarla al iniciar sesión.</p>
        <div className="d-flex gap-2 mb-2">
          <Form.Control readOnly value={password} style={{ fontFamily: 'monospace', fontSize: '1.2rem' }} onFocus={e => e.target.select()} />
          <Button variant={copied === 'pw' ? 'success' : 'outline-primary'} onClick={() => copy('pw', password)}>{copied === 'pw' ? 'Copiada' : 'Copiar'}</Button>
        </div>
        <Button size="sm" variant={copied === 'msg' ? 'success' : 'outline-secondary'} onClick={() => copy('msg', message)}>
          <i className="bi bi-clipboard me-1" />{copied === 'msg' ? 'Mensaje copiado' : 'Copiar usuario + contraseña + enlace'}
        </Button>

        <hr />
        <Form.Label className="small text-muted mb-1">Enviar por correo</Form.Label>
        <div className="d-flex gap-2">
          <Form.Control type="email" placeholder="correo@ejemplo.com" value={email} disabled={!!sentTo}
            onChange={e => { setEmail(e.target.value); setError(''); }} />
          <Button disabled={sending || !!sentTo || !EMAIL_RE.test(email.trim())} onClick={send}>
            <i className="bi bi-envelope me-1" />{sending ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
        {sentTo && <div className="alert alert-success mt-2 mb-0 py-2">Correo enviado a <strong>{sentTo}</strong>.</div>}
        {error && <div className="alert alert-danger mt-2 mb-0 py-2">{error}</div>}
      </Modal.Body>
      <Modal.Footer><Button onClick={onClose}>Listo</Button></Modal.Footer>
    </Modal>
  );
};

const AdminUsersTab = () => {
  const { user: me, renamed } = useAuth();
  const [users, setUsers] = useState([]);
  const [gatedActions, setGatedActions] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [adding, setAdding] = useState(false);
  const [resetting, setResetting] = useState(null);
  const [editingPhone, setEditingPhone] = useState(null);
  const [renaming, setRenaming] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [revealed, setRevealed] = useState(null);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editingPermissions, setEditingPermissions] = useState(null);

  const load = useCallback(() => {
    API.get('/users').then(r => setUsers(r.data)).catch(err => setError(err.response?.data?.message || 'No se pudieron cargar los usuarios.'));
  }, []);
  useEffect(load, [load]);
  useEffect(() => { API.get('/users/gated-actions').then(r => setGatedActions(r.data)).catch(() => {}); }, []);

  const act = async (fn, success) => {
    setError(''); setNotice('');
    try { await fn(); if (success) setNotice(success); load(); return true; }
    catch (err) { setError(err.response?.data?.message || 'La acción falló.'); return false; }
  };

  return (
    <Card className="shadow-sm p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="text-muted small">Solo los administradores ven esta sección. Cada cambio queda registrado en Actividad.</div>
        <Button onClick={() => setAdding(true)}><i className="bi bi-person-plus" /> Nuevo usuario</Button>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}

      <Table hover responsive className="mb-0 align-middle">
        <thead><tr><th>Usuario</th><th>Correo / WhatsApp</th><th style={{ width: 130 }}>Rol</th><th>Estado</th><th>Último acceso</th><th>Creado</th><th style={{ width: 300 }}></th></tr></thead>
        <tbody>
          {users.map(u => {
            const isMe = u.username === me.username;
            // Owners can only be changed by themselves, and only an owner can manage other admins (the server enforces it).
            const locked = !isMe && (u.isOwner || (u.role === 'admin' && !me.isOwner));
            const lockedWhy = u.isOwner ? 'Cuenta de propietario: solo su titular puede cambiarla' : 'Solo un propietario puede administrar administradores';
            return (
              <tr key={u._id}>
                <td>
                  {u.username}
                  {me.isOwner && !locked && <Button variant="link" size="sm" className="p-0 ms-1" title="Cambiar nombre de usuario" onClick={() => setRenaming(u)}><i className="bi bi-pencil" /></Button>}
                  {' '}{isMe && <Badge bg="info">tú</Badge>} {u.isOwner && <Badge bg="warning" text="dark" title="Propietario del sistema">propietario</Badge>}
                </td>
                <td className="small">
                  {u.email
                    ? <>{u.email} {!locked && <Button variant="link" size="sm" className="p-0 ms-1" title="Cambiar correo" onClick={() => setEditingEmail(u)}><i className="bi bi-pencil" /></Button>}</>
                    : <Button variant="link" size="sm" className="p-0" disabled={locked} onClick={() => setEditingEmail(u)}>+ Agregar correo</Button>}
                  <div>
                    {u.whatsappPhone
                      ? <>
                          <i className="bi bi-whatsapp text-muted" /> +{u.whatsappPhone}
                          {u.whatsappExtraPhones?.length > 0 && <span className="text-muted" title="También le responde el asistente desde estos números; las alertas llegan al principal"> · {u.whatsappExtraPhones.map(p => `+${p}`).join(' · ')}</span>}
                          {!locked && <Button variant="link" size="sm" className="p-0 ms-1" title="Cambiar WhatsApp" onClick={() => setEditingPhone(u)}><i className="bi bi-pencil" /></Button>}
                        </>
                      : <Button variant="link" size="sm" className="p-0" disabled={locked} onClick={() => setEditingPhone(u)}>+ Agregar WhatsApp</Button>}
                  </div>
                </td>
                <td>
                  <Form.Select size="sm" value={u.role} disabled={isMe || locked} title={isMe ? 'No puedes cambiar tu propio rol' : locked ? lockedWhy : undefined}
                    onChange={e => act(() => API.patch(`/users/${u._id}`, { role: e.target.value }), `Rol de ${u.username} actualizado.`)}>
                    <option value="sales">Ventas</option>
                    <option value="admin" disabled={!me.isOwner && u.role !== 'admin'}>Administrador</option>
                  </Form.Select>
                </td>
                <td>
                  {u.active === false ? <Badge bg="secondary">Desactivado</Badge> : <Badge bg="success">Activo</Badge>}
                  {u.mustChangePassword && <Badge bg="warning" text="dark" className="ms-1">Debe cambiar contraseña</Badge>}
                </td>
                <td className="small">{u.lastLoginAt ? dateTime(u.lastLoginAt) : <span className="text-muted">Nunca</span>}</td>
                <td className="small text-muted">{dateTime(u.createdAt)}{u.createdBy && <> · por {u.createdBy}</>}</td>
                <td className="text-end">
                  <div className="d-flex gap-1 justify-content-end">
                    {u.role === 'sales' && (
                      <Button size="sm" variant="outline-info" disabled={locked} title={locked ? lockedWhy : 'Excepciones a Aprobaciones para este usuario'} onClick={() => setEditingPermissions(u)}>
                        Permisos{u.trustedActions?.length > 0 && <Badge bg="info" className="ms-1">{u.trustedActions.length}</Badge>}
                      </Button>
                    )}
                    <Button size="sm" variant="outline-secondary" disabled={locked} title={locked ? lockedWhy : undefined} onClick={() => setResetting(u)}>Restablecer contraseña</Button>
                    <Button size="sm" variant="outline-warning" disabled={isMe || locked} title={locked ? lockedWhy : undefined}
                      onClick={() => act(() => API.patch(`/users/${u._id}`, { active: u.active === false }), `${u.username} ${u.active === false ? 'activado' : 'desactivado'}.`)}>
                      {u.active === false ? 'Activar' : 'Desactivar'}
                    </Button>
                    <Button size="sm" variant="outline-danger" disabled={isMe || locked} title={locked ? lockedWhy : undefined} onClick={() => setDeleting(u)}>Eliminar</Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {adding && (
        <NewUserModal
          canCreateAdmin={!!me.isOwner}
          onClose={() => setAdding(false)}
          onCreate={async (form) => {
            let created;
            const ok = await act(async () => { created = (await API.post('/users', form)).data.user; }, `Usuario ${form.username} creado.`);
            if (ok) { setAdding(false); if (form.mustChangePassword) setRevealed({ title: 'Usuario creado', userId: created.id, username: form.username, email: form.email, password: form.password }); }
            return ok;
          }}
        />
      )}

      {resetting && (
        <Modal show onHide={() => setResetting(null)} centered>
          <Modal.Header closeButton><Modal.Title>Restablecer contraseña — {resetting.username}</Modal.Title></Modal.Header>
          <Modal.Body>
            Se genera una contraseña temporal nueva. Las sesiones abiertas de {resetting.username} se cierran de inmediato y tendrá que elegir su propia contraseña al entrar.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setResetting(null)}>Cancelar</Button>
            <Button onClick={async () => {
              const target = resetting;
              setResetting(null);
              setError('');
              try {
                const { data } = await API.post(`/users/${target._id}/reset-password`, {});
                setRevealed({ title: 'Contraseña restablecida', userId: target._id, username: target.username, email: target.email, password: data.temporaryPassword });
                load();
              } catch (err) { setError(err.response?.data?.message || 'No se pudo restablecer la contraseña.'); }
            }}>Generar contraseña temporal</Button>
          </Modal.Footer>
        </Modal>
      )}

      {deleting && (
        <Modal show onHide={() => setDeleting(null)} centered>
          <Modal.Header closeButton><Modal.Title>Eliminar a {deleting.username}</Modal.Title></Modal.Header>
          <Modal.Body>
            La cuenta se elimina y su sesión se cierra al instante. Su historial de actividad y los documentos que creó se conservan.
            Si solo quieres impedir que entre, usa <strong>Desactivar</strong> (se puede revertir).
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setDeleting(null)}>Cancelar</Button>
            <Button variant="danger" onClick={async () => { const target = deleting; setDeleting(null); await act(() => API.delete(`/users/${target._id}`), `Usuario ${target.username} eliminado.`); }}>Eliminar definitivamente</Button>
          </Modal.Footer>
        </Modal>
      )}

      {revealed && <PasswordReveal {...revealed} onClose={() => setRevealed(null)} onEmailSaved={load} />}
      {editingEmail && (
        <EmailModal user={editingEmail} onClose={() => setEditingEmail(null)}
          onSave={async (email) => { const ok = await act(() => API.patch(`/users/${editingEmail._id}`, { email }), `Correo de ${editingEmail.username} ${email ? 'actualizado' : 'eliminado'}.`); if (ok) setEditingEmail(null); return ok; }} />
      )}
      {renaming && (
        <RenameModal user={renaming} isMe={renaming.username === me.username} onClose={() => setRenaming(null)}
          onSave={async (username) => {
            const target = renaming;
            const ok = await act(() => API.patch(`/users/${target._id}/username`, { username }), `${target.username} ahora se llama ${username}.`);
            if (ok) { if (target.username === me.username) renamed(username); setRenaming(null); }
            return ok;
          }} />
      )}
      {editingPhone && (
        <PhoneModal user={editingPhone} onClose={() => setEditingPhone(null)}
          onSave={async (whatsappPhone, whatsappExtraPhones) => { const ok = await act(() => API.patch(`/users/${editingPhone._id}`, { whatsappPhone, whatsappExtraPhones }), `WhatsApp de ${editingPhone.username} ${whatsappPhone || whatsappExtraPhones.length ? 'actualizado' : 'eliminado'}.`); if (ok) setEditingPhone(null); return ok; }} />
      )}
      {editingPermissions && (
        <PermissionsModal user={editingPermissions} actions={gatedActions} onClose={() => setEditingPermissions(null)}
          onSave={async (trustedActions) => {
            const ok = await act(() => API.patch(`/users/${editingPermissions._id}/permissions`, { trustedActions }), `Permisos de ${editingPermissions.username} actualizados.`);
            if (ok) setEditingPermissions(null);
            return ok;
          }} />
      )}
    </Card>
  );
};

const NewUserModal = ({ onClose, onCreate, canCreateAdmin }) => {
  const [form, setForm] = useState({ username: '', email: '', role: 'sales', password: generatePassword(), mustChangePassword: true });
  const [busy, setBusy] = useState(false);
  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>Nuevo usuario</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Usuario <span className="text-muted small">(3–30: letras, números, . _ -)</span></Form.Label>
          <Form.Control value={form.username} onChange={e => set({ username: e.target.value })} autoFocus />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Correo <span className="text-muted small">(opcional; para enviarle su contraseña)</span></Form.Label>
          <Form.Control type="email" value={form.email} onChange={e => set({ email: e.target.value })} placeholder="correo@ejemplo.com" />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Rol</Form.Label>
          <Form.Select value={form.role} onChange={e => set({ role: e.target.value })}>
            <option value="sales">Ventas</option>
            <option value="admin" disabled={!canCreateAdmin}>Administrador (acceso total, incluido este panel){canCreateAdmin ? '' : ' — solo un propietario'}</option>
          </Form.Select>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Contraseña inicial</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control value={form.password} style={{ fontFamily: 'monospace' }} onChange={e => set({ password: e.target.value })} />
            <Button variant="outline-secondary" onClick={() => set({ password: generatePassword() })}>Generar</Button>
          </div>
        </Form.Group>
        <Form.Check type="checkbox" id="must-change" label="Debe elegir su propia contraseña al primer inicio de sesión (recomendado)"
          checked={form.mustChangePassword} onChange={e => set({ mustChangePassword: e.target.checked })} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancelar</Button>
        <Button disabled={busy || !form.username.trim() || form.password.length < 8 || (form.email.trim() !== '' && !EMAIL_RE.test(form.email.trim()))}
          onClick={async () => { setBusy(true); await onCreate({ ...form, username: form.username.trim(), email: form.email.trim().toLowerCase() }); setBusy(false); }}>
          {busy ? 'Creando...' : 'Crear usuario'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const EmailModal = ({ user, onClose, onSave }) => {
  const [email, setEmail] = useState(user.email || '');
  const [busy, setBusy] = useState(false);
  const valid = email.trim() === '' || EMAIL_RE.test(email.trim());
  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>Correo de {user.username}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Control type="email" autoFocus value={email} placeholder="correo@ejemplo.com" onChange={e => setEmail(e.target.value)} />
        <div className="small text-muted mt-2">Solo se usa para enviarle contraseñas temporales. Déjalo vacío para quitarlo.</div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancelar</Button>
        <Button disabled={busy || !valid} onClick={async () => { setBusy(true); await onSave(email.trim().toLowerCase()); setBusy(false); }}>Guardar</Button>
      </Modal.Footer>
    </Modal>
  );
};

// Per-user exceptions to Aprobaciones (services/approvals.js ACTIONS): a checked action goes
// through directly for this one sales user instead of waiting on an admin — the same trust an
// admin extends by hand today, just recorded so it doesn't need repeating every time.
const PermissionsModal = ({ user, actions, onClose, onSave }) => {
  const [checked, setChecked] = useState(new Set(user.trustedActions || []));
  const [busy, setBusy] = useState(false);
  const toggle = (key) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <Modal show onHide={busy ? undefined : onClose} centered>
      <Modal.Header closeButton><Modal.Title>Permisos de {user.username}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p className="text-muted small">
          Estas acciones normalmente piden la aprobación de un administrador (ver Aprobaciones). Marca las que{' '}
          <strong>{user.username}</strong> puede hacer directamente, sin esperar aprobación.
        </p>
        {actions.length === 0 ? (
          <div className="text-muted small">Cargando...</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            {actions.map(a => (
              <Form.Check key={a.key} type="checkbox" id={`trust-${a.key}`} label={a.label}
                checked={checked.has(a.key)} onChange={() => toggle(a.key)} />
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={busy} onClick={onClose}>Cancelar</Button>
        <Button disabled={busy} onClick={async () => { setBusy(true); const ok = await onSave([...checked]); if (!ok) setBusy(false); }}>
          {busy ? 'Guardando...' : 'Guardar permisos'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// A staff member's WhatsApp: an administrator with one can ask Intmed's WhatsApp assistant (on Intmed's own number) for
// Intmed's numbers — sales, collections, rentals and inventory — and record gastos, facturas, quotes and letters.
// Someone with a second phone adds it as another number: the assistant answers them the same from either, and alerts go
// to the main one. The server normalizes each (adds the 1 to a Dominican 10-digit number), drops repeats, and refuses a
// number another user already has. Ported from Caribbean Gas' Ventas.
const MAX_EXTRA_PHONES = 4;
const phoneDigits = (p) => p.replace(/\D/g, '');
const phoneValid = (p) => { const d = phoneDigits(p); return d === '' || (d.length >= 10 && d.length <= 15); };

const PhoneModal = ({ user, onClose, onSave }) => {
  const [phone, setPhone] = useState(user.whatsappPhone || '');
  const [extras, setExtras] = useState(user.whatsappExtraPhones || []);
  const [busy, setBusy] = useState(false);
  const setExtra = (i, value) => setExtras(list => list.map((p, j) => (j === i ? value : p)));
  const valid = phoneValid(phone) && extras.every(phoneValid);
  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>WhatsApp de {user.username}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Label className="small text-muted mb-1">Número principal <span className="text-muted">(aquí llegan las alertas)</span></Form.Label>
        <Form.Control type="tel" autoFocus value={phone} placeholder="809 555 1234" onChange={e => setPhone(e.target.value)} />
        {extras.length > 0 && <Form.Label className="small text-muted mt-3 mb-1">Otros números</Form.Label>}
        {extras.map((p, i) => (
          <div key={i} className="d-flex gap-2 mb-2">
            <Form.Control type="tel" value={p} placeholder="829 555 1234" onChange={e => setExtra(i, e.target.value)} />
            <Button variant="outline-danger" title="Quitar este número" onClick={() => setExtras(list => list.filter((_, j) => j !== i))}><i className="bi bi-x-lg" /></Button>
          </div>
        ))}
        {extras.length < MAX_EXTRA_PHONES && (
          <Button variant="link" size="sm" className="p-0 mt-2" onClick={() => setExtras(list => [...list, ''])}>+ Agregar otro número</Button>
        )}
        <div className="small text-muted mt-2">
          {user.role === 'admin'
            ? 'Desde estos números podrá pedirle al asistente de WhatsApp de Intmed los números de la empresa (ventas, cobros, cuentas por cobrar, alquileres e inventario) y registrar gastos, facturas, cotizaciones y cartas. Desde cualquiera de ellos es la misma persona.'
            : 'El asistente de WhatsApp solo da los números de la empresa a administradores; a un usuario de ventas lo atiende como a cualquier persona.'}
          {' '}Deja un campo vacío para quitar ese número.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancelar</Button>
        <Button disabled={busy || !valid} onClick={async () => { setBusy(true); await onSave(phoneDigits(phone), extras.map(phoneDigits).filter(Boolean)); setBusy(false); }}>Guardar</Button>
      </Modal.Footer>
    </Modal>
  );
};

// Renaming an account (owners only): the person keeps their sessions, password and settings and signs in with the new
// name from now on. What they did before keeps the old name in Actividad and on documents.
const USERNAME_RE = /^[a-zA-Z0-9._-]{3,30}$/;

const RenameModal = ({ user, isMe, onClose, onSave }) => {
  const [username, setUsername] = useState(user.username);
  const [busy, setBusy] = useState(false);
  const name = username.trim();
  const valid = USERNAME_RE.test(name) && name !== user.username;
  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton><Modal.Title>Cambiar nombre de {user.username}</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Label>Nuevo nombre de usuario <span className="text-muted small">(3–30: letras, números, . _ -)</span></Form.Label>
        <Form.Control autoFocus value={username} onChange={e => setUsername(e.target.value)} />
        <div className="small text-muted mt-2">
          {isMe ? 'Tu sesión sigue abierta; la próxima vez entras' : 'Su sesión sigue abierta; la próxima vez entra'} con el nombre nuevo, escrito
          igual (mayúsculas incluidas). La contraseña no cambia. Lo que {isMe ? 'hiciste' : 'hizo'} antes sigue apareciendo con el nombre
          anterior en Actividad y en los documentos.
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Cancelar</Button>
        <Button disabled={busy || !valid} onClick={async () => { setBusy(true); const ok = await onSave(name); if (!ok) setBusy(false); }}>
          {busy ? 'Guardando...' : 'Cambiar nombre'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AdminUsersTab;
